import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Alert,
  Snackbar
} from '@mui/material';
import { z } from 'zod';
import { Contact } from '../../types';
import { RequiredFields } from './RequiredFields';
import { OptionalFields } from './OptionalFields';
import { FormActions } from './FormActions';
import { contactSchema, ContactFormData, INITIAL_FORM_DATA } from './validation';

interface ContactFormProps {
  contact?: Contact;
  onSubmit: (contact: ContactFormData) => void;
  onCancel: () => void;
}

export const ContactForm: React.FC<ContactFormProps> = ({
  contact,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState<ContactFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (contact) {
      setFormData({
        ...INITIAL_FORM_DATA,
        ...contact,
        primaryPhone: contact.phone,
      });
    }
  }, [contact]);

  const handleChange = (field: keyof ContactFormData) => (
    event: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when field is modified
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    
    try {
      const validatedData = contactSchema.parse(formData);
      onSubmit(validatedData);
      setShowSuccess(true);
      if (!contact) {
        setFormData(INITIAL_FORM_DATA);
      }
      setErrors({});
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Partial<Record<keyof ContactFormData, string>> = {};
        error.errors.forEach(err => {
          const path = err.path[0] as keyof ContactFormData;
          newErrors[path] = err.message;
        });
        setErrors(newErrors);
      }
    }
  };

  const handleCancel = () => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    onCancel();
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <RequiredFields
          formData={formData}
          errors={errors}
          onChange={handleChange}
        />
        
        <OptionalFields
          formData={formData}
          errors={errors}
          onChange={handleChange}
        />

        <FormActions
          onCancel={handleCancel}
          isEdit={!!contact}
        />
      </Grid>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          Contact {contact ? 'updated' : 'added'} successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};